/**
 * Owner-view Conversations tab body (tab 2 of 4).
 *
 * A calm, read-only log of what the genie has been talking about. Channel chips
 * to glance by phone / web, then plain rows — who, the gist, when, and how it
 * ended (resolved / handed to you / message taken / lead captured). No reply
 * controls; the only mutating door in this flow is "Tell our genie", which lives
 * in the shared GenieOwnerDetail top bar — this body never mutates.
 *
 * Matches OverviewTab's SectionCard rhythm so the four tabs feel like one
 * surface. Data: stub `useGenieConversations(id)` (shaped to the eventual
 * ConversationService.list contract). Brand law: genie tokens only; wireframe
 * green to genie-success; "Help Genie" two words; *Voice* italic; no "AI" copy.
 */
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GenieDisc, SectionCard } from "@/components/voice-genie/atoms";
import { useGenieConversations } from "@/hooks/voice-vision";
import type { Conversation, ConversationChannel } from "@/types/voice-vision";
import {
  ConversationsChips,
  ConversationsEmpty,
  ConversationsRow,
  type ConversationsFilterKey,
} from "./ConversationsList";

const PINK = "#FF4DB8";

/* ─────────────────────── filter <-> channel mapping ─────────────────────── */

// Phone is its own chip; everything else (web/qr/link) reads as "Website chat".
const matchesFilter = (filter: ConversationsFilterKey, channel: ConversationChannel) => {
  if (filter === "all") return true;
  if (filter === "phone") return channel === "phone";
  return channel !== "phone"; // "web"
};

/* ─────────────────────────────── skeleton ──────────────────────────────── */

const ConversationsSkeleton = () => (
  <div className="w-full">
    {/* chips skeleton */}
    <div className="flex gap-2 mb-3.5">
      {[64, 84, 116].map((w, i) => (
        <div
          key={i}
          className="h-[34px] rounded-full bg-gray-100 animate-pulse"
          style={{ width: w }}
        />
      ))}
    </div>
    {/* list skeleton — 6 rows inside the section card frame */}
    <SectionCard
      genieIcon="chat"
      tint={PINK}
      title="Recent conversations"
      subtitle="What your genie has been talking about"
    >
      <div className="flex flex-col">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`flex items-center gap-3.5 py-3.5 ${i === 0 ? "" : "border-t border-[#F3F4F6]"}`}
          >
            <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="h-3.5 w-2/3 rounded bg-gray-100 animate-pulse" />
              <div className="h-2.5 w-1/3 rounded bg-gray-100 animate-pulse mt-2" />
            </div>
            <div className="h-3 w-24 rounded bg-gray-100 animate-pulse flex-shrink-0" />
          </div>
        ))}
      </div>
    </SectionCard>
  </div>
);

/* ──────────────────────────────── tab ──────────────────────────────────── */

const ConversationsTab = () => {
  const { id = "" } = useParams();
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGenieConversations(id);
  const [filter, setFilter] = useState<ConversationsFilterKey>("all");

  const conversations: Conversation[] = data?.data ?? [];

  // Counts per chip, computed once from the full list.
  const counts = useMemo(() => {
    const all = conversations.length;
    const phone = conversations.filter((c) => c.channel === "phone").length;
    return { all, phone, web: all - phone };
  }, [conversations]);

  const visible = useMemo(
    () => conversations.filter((c) => matchesFilter(filter, c.channel)),
    [conversations, filter],
  );

  if (isLoading) return <ConversationsSkeleton />;

  if (isError) {
    return (
      <SectionCard
        genieIcon="chat"
        tint={PINK}
        title="Recent conversations"
        subtitle="What your genie has been talking about"
      >
        <div className="flex flex-col items-center text-center py-10 px-4">
          <div className="mb-3">
            <GenieDisc size={64} />
          </div>
          <p className="text-sm font-bold text-[#222B45]">
            We couldn't load your conversations
          </p>
          <p className="text-[13px] text-gray-500 mt-1 max-w-[360px]">
            Please try again in a moment.
          </p>
        </div>
      </SectionCard>
    );
  }

  // Empty across the whole genie — no chips, just the invite.
  if (conversations.length === 0) {
    return (
      <div className="w-full">
        <ConversationsEmpty scope="genie" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* ─────── Channel chips ─────── */}
      <ConversationsChips active={filter} counts={counts} onChange={setFilter} />

      {/* ─────── Conversation list ─────── */}
      <SectionCard
        genieIcon="chat"
        tint={PINK}
        title="Recent conversations"
        subtitle="What your genie has been talking about"
      >
        {visible.length === 0 ? (
          <ConversationsEmpty scope="filter" />
        ) : (
          <div className="flex flex-col">
            {visible.map((c, i) => (
              <ConversationsRow key={c.id} conversation={c} first={i === 0} />
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="flex justify-center pt-3.5 border-t border-[#F3F4F6]">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="h-9 px-4 rounded-full border border-genie-border text-[13px] font-semibold text-gray-500 hover:border-genie-primary/40 hover:text-[#222B45] transition-all disabled:opacity-60"
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </SectionCard>

      {/* ─────── read-only note ─────── */}
      <p className="text-[12.5px] text-gray-500 text-center mt-4 leading-[1.5]">
        This is a read-only view. To change how your genie replies, just tell our genie.
      </p>
    </div>
  );
};

export default ConversationsTab;
